<template>
  <div class="q-pa-md">
    <q-card>
      <q-card-section>
        <div class="row items-center justify-between">
          <div class="text-h6">{{ t('users.title') }}</div>
          <q-btn
              color="primary"
              icon="add"
              :label="t('users.add_user')"
              @click="showAddDialog = true"
          />
        </div>
      </q-card-section>

      <q-separator />

      <q-card-section>
        <q-table
            :rows="users"
            :columns="columns"
            row-key="id"
            flat
            bordered
            :loading="loading"
            dense
        >
          <template v-slot:body-cell-name="props">
            <q-td :props="props">
              <div>{{ props.row.name || props.row.username }}</div>
              <div class="text-caption text-grey">@{{ props.row.username }}</div>
            </q-td>
          </template>

          <template v-slot:body-cell-role="props">
            <q-td :props="props">
              <q-chip
                  :color="props.row.role === 'admin' ? 'primary' : 'secondary'"
                  text-color="white"
                  size="sm"
              >
                {{ props.row.role === 'admin' ? t('users.role.admin') : t('users.role.user') }}
              </q-chip>
            </q-td>
          </template>

          <template v-slot:body-cell-status="props">
            <q-td :props="props">
              <q-chip
                  :color="props.row.is_blocked ? 'negative' : 'positive'"
                  text-color="white"
                  size="sm"
              >
                {{ props.row.is_blocked ? t('users.status.blocked') : t('users.status.active') }}
              </q-chip>
            </q-td>
          </template>

          <template v-slot:body-cell-last_login="props">
            <q-td :props="props">
              {{ formatDate(props.row.last_login_at) }}
            </q-td>
          </template>

          <template v-slot:body-cell-actions="props">
            <q-td :props="props">
              <div class="q-gutter-xs row no-wrap">
                <q-btn
                    icon="edit"
                    size="sm"
                    flat
                    round
                    @click="editUser(props.row)"
                >
                  <q-tooltip>{{ t('users.actions.edit') }}</q-tooltip>
                </q-btn>
                <q-btn
                    icon="lock"
                    size="sm"
                    flat
                    round
                    :color="props.row.is_blocked ? 'positive' : 'negative'"
                    @click="toggleBlock(props.row)"
                    :disable="props.row.id === currentUserId"
                >
                  <q-tooltip>{{ props.row.id === currentUserId ? t('users.actions.cannot_block_self') : (props.row.is_blocked ? t('users.actions.unblock') : t('users.actions.block')) }}</q-tooltip>
                </q-btn>
                <q-btn
                    icon="key"
                    size="sm"
                    flat
                    round
                    color="warning"
                    @click="changePassword(props.row)"
                >
                  <q-tooltip>{{ t('users.actions.change_password') }}</q-tooltip>
                </q-btn>
                <q-btn
                    icon="delete"
                    size="sm"
                    flat
                    round
                    color="negative"
                    @click="deleteUser(props.row)"
                    :disable="props.row.id === currentUserId"
                >
                  <q-tooltip>{{ props.row.id === currentUserId ? t('users.actions.cannot_delete_self') : t('users.actions.delete') }}</q-tooltip>
                </q-btn>
              </div>
            </q-td>
          </template>
        </q-table>
      </q-card-section>
    </q-card>

    <!-- Диалог добавления пользователя -->
    <q-dialog v-model="showAddDialog" persistent>
      <q-card style="min-width: 400px">
        <q-card-section>
          <div class="text-h6">{{ t('users.dialogs.add.title') }}</div>
        </q-card-section>

        <q-card-section class="q-pt-none">
          <q-form @submit="createUser" class="q-gutter-md">
            <q-input
                v-model="newUser.username"
                :label="t('users.fields.username')"
                outlined
                required
                :rules="[val => !!val || t('validation.required')]"
            />
            <q-input
                v-model="newUser.name"
                :label="t('users.fields.full_name')"
                outlined
            />
            <q-input
                v-model="newUser.password"
                :label="t('users.fields.password')"
                type="password"
                outlined
                required
                :rules="[val => !!val || t('validation.required')]"
            />
            <q-select
                v-model="newUser.role"
                :options="roleOptions"
                :label="t('users.fields.role')"
                outlined
                required
                emit-value
                map-options
            />
          </q-form>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat :label="t('common.cancel')" @click="showAddDialog = false" />
          <q-btn
              color="primary"
              :label="t('users.dialogs.add.create')"
              @click="createUser"
              :loading="creating"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог редактирования -->
    <q-dialog v-model="showEditDialog" persistent>
      <q-card style="min-width: 400px">
        <q-card-section>
          <div class="text-h6">{{ t('users.dialogs.edit.title') }}</div>
        </q-card-section>

        <q-card-section class="q-pt-none">
          <q-form @submit="updateUser" class="q-gutter-md">
            <q-input
                v-model="editingUser.username"
                :label="t('users.fields.username')"
                outlined
                required
            />
            <q-input
                v-model="editingUser.name"
                :label="t('users.fields.full_name')"
                outlined
            />
            <q-select
                v-model="editingUser.role"
                :options="roleOptions"
                :label="t('users.fields.role')"
                outlined
                required
                emit-value
                map-options
            />
          </q-form>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat :label="t('common.cancel')" @click="showEditDialog = false" />
          <q-btn
              color="primary"
              :label="t('common.save')"
              @click="updateUser"
              :loading="updating"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог смены пароля -->
    <q-dialog v-model="showPasswordDialog" persistent>
      <q-card style="min-width: 400px">
        <q-card-section>
          <div class="text-h6">{{ t('users.dialogs.password.title') }}</div>
        </q-card-section>

        <q-card-section class="q-pt-none">
          <q-input
              v-model="newPassword"
              :label="t('users.dialogs.password.new_password')"
              type="password"
              outlined
              required
          />
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat :label="t('common.cancel')" @click="showPasswordDialog = false" />
          <q-btn
              color="warning"
              :label="t('users.dialogs.password.change')"
              @click="updatePassword"
              :loading="changingPassword"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useQuasar } from 'quasar'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import api from '@/api/api'

const $q = useQuasar()
const { t } = useI18n()
const auth = useAuthStore()

const users = ref([])
const loading = ref(false)
const creating = ref(false)
const updating = ref(false)
const changingPassword = ref(false)

const showAddDialog = ref(false)
const showEditDialog = ref(false)
const showPasswordDialog = ref(false)

const newUser = ref({
  username: '',
  name: '',
  password: '',
  role: 'user'
})

const editingUser = ref({})
const passwordUser = ref({})
const newPassword = ref('')

const currentUserId = computed(() => auth.user?.id)

const columns = computed(() => [
  { name: 'id', label: 'ID', field: 'id', sortable: true, align: 'left' },
  { name: 'name', label: t('users.table.user'), field: 'name', sortable: true, align: 'left' },
  { name: 'role', label: t('users.table.role'), field: 'role', sortable: true, align: 'center' },
  { name: 'status', label: t('users.table.status'), field: 'is_blocked', sortable: true, align: 'center' },
  { name: 'last_login', label: t('users.table.last_login'), field: 'last_login_at', sortable: true, align: 'left' },
  { name: 'actions', label: t('users.table.actions'), align: 'center' }
])

const roleOptions = computed(() => [
  { label: t('users.role.user'), value: 'user' },
  { label: t('users.role.admin'), value: 'admin' }
])

const fetchUsers = async () => {
  loading.value = true
  try {
    const { data } = await api.get('/api/users')
    users.value = data
  } catch (error) {
    $q.notify({
      type: 'negative',
      message: t('users.notifications.load_error'),
      caption: error.message
    })
  } finally {
    loading.value = false
  }
}

const createUser = async () => {
  if (!newUser.value.username || !newUser.value.password) {
    $q.notify({
      type: 'negative',
      message: t('users.notifications.fill_required_fields')
    })
    return
  }

  creating.value = true
  try {
    await api.post('/api/users', newUser.value)

    $q.notify({
      type: 'positive',
      message: t('users.notifications.user_created')
    })

    showAddDialog.value = false
    newUser.value = { username: '', name: '', password: '', role: 'user' }
    fetchUsers()
  } catch (error) {
    $q.notify({
      type: 'negative',
      message: t('users.notifications.create_error'),
      caption: error.message
    })
  } finally {
    creating.value = false
  }
}

const editUser = (user) => {
  editingUser.value = { ...user }
  showEditDialog.value = true
}

const updateUser = async () => {
  updating.value = true
  try {
    await api.put('/api/users', {
      id: editingUser.value.id,
      username: editingUser.value.username,
      name: editingUser.value.name,
      role: editingUser.value.role
    })

    $q.notify({
      type: 'positive',
      message: t('users.notifications.user_updated')
    })

    showEditDialog.value = false
    fetchUsers()
  } catch (error) {
    $q.notify({
      type: 'negative',
      message: t('users.notifications.update_error'),
      caption: error.message
    })
  } finally {
    updating.value = false
  }
}

const toggleBlock = async (user) => {
  try {
    const action = user.is_blocked ? t('users.actions.unblock_action') : t('users.actions.block_action')

    $q.dialog({
      title: t('users.confirmations.title'),
      message: t('users.confirmations.toggle_block', { action, username: user.username }),
      cancel: true,
      persistent: true
    }).onOk(async () => {
      try {
        await api.put('/api/users', {
          id: user.id,
          action: 'toggle_block'
        })

        $q.notify({
          type: 'positive',
          message: t('users.notifications.user_status_changed', {
            status: user.is_blocked ? t('users.status.unblocked') : t('users.status.blocked_action')
          })
        })

        fetchUsers()
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: t('users.notifications.status_change_error'),
          caption: error.message
        })
      }
    })
  } catch (error) {
    console.error(error)
  }
}

const changePassword = (user) => {
  passwordUser.value = user
  newPassword.value = ''
  showPasswordDialog.value = true
}

const updatePassword = async () => {
  if (!newPassword.value) {
    $q.notify({
      type: 'negative',
      message: t('users.notifications.enter_new_password')
    })
    return
  }

  changingPassword.value = true
  try {
    await api.put('/api/users', {
      id: passwordUser.value.id,
      action: 'change_password',
      password: newPassword.value
    })

    $q.notify({
      type: 'positive',
      message: t('users.notifications.password_changed')
    })

    showPasswordDialog.value = false
    newPassword.value = ''
  } catch (error) {
    $q.notify({
      type: 'negative',
      message: t('users.notifications.password_change_error'),
      caption: error.message
    })
  } finally {
    changingPassword.value = false
  }
}

const deleteUser = (user) => {
  $q.dialog({
    title: t('users.confirmations.delete_title'),
    message: t('users.confirmations.delete_message', { username: user.username }),
    cancel: true,
    persistent: true,
    color: 'negative'
  }).onOk(async () => {
    try {
      await api.delete(`/api/users?id=${user.id}`)

      $q.notify({
        type: 'positive',
        message: t('users.notifications.user_deleted')
      })

      fetchUsers()
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: t('users.notifications.delete_error'),
        caption: error.message
      })
    }
  })
}

const formatDate = (dateStr) => {
  if (!dateStr) return t('users.never')

  try {
    return new Date(dateStr).toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return t('users.date_error')
  }
}

onMounted(fetchUsers)
</script>